/** API client for POST /api/v1/ask (SSE streaming) and conversation CRUD. */

import type { ApiClient } from './client.js';
import { parseSseFrames } from '../views/ask/sse-parser.js';
import type { ChartSpec } from '../views/ask/chart-renderer.js';

/**
 * Provider for the current CSRF token. Set after sign-in via
 * setAskCsrfTokenProvider so /api/v1/ask requests carry X-CSRF-Token.
 */
let askCsrfTokenProvider: (() => string | null) | null = null;

export function setAskCsrfTokenProvider(provider: () => string | null): void {
  askCsrfTokenProvider = provider;
}

export type AskMessageRole = 'user' | 'assistant';
export type AskConfidence = 'low' | 'medium' | 'high';
export type ExternalSourceTrustTier = 'authoritative' | 'established' | 'blog_or_other';

export interface ExternalSource {
  source_id: number;
  title: string;
  url: string;
  domain: string;
  snippet: string;
  score: number | null;
  trust_tier: ExternalSourceTrustTier;
  provider: 'tavily';
  retrieved_at: string;
}

export interface AskConversationSummary {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  last_message_at?: string | null;
  message_count: number;
  last_message_preview?: string | null;
}

export interface AskConversationMessage {
  id: string;
  role: AskMessageRole;
  sequence: number;
  content: string;
  chart_specs: ChartSpec[];
  sources: ExternalSource[];
  /** Present on GET; web Ask UI ignores (iOS-only surface, DEC-41). */
  follow_ups?: string[];
  confidence: AskConfidence | null;
  created_at: string;
}

export interface AskConversationListResponse {
  items: AskConversationSummary[];
  next_cursor: string | null;
}

export interface AskConversationDetailResponse {
  conversation: AskConversationSummary;
  messages: AskConversationMessage[];
}

export interface AskRenameResponse {
  conversation: AskConversationSummary;
}

export interface AskConversationStreamEvent {
  type: 'conversation';
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export type AskStreamEvent =
  | AskConversationStreamEvent
  | { type: 'thinking'; text: string }
  | { type: 'chart'; spec: ChartSpec }
  | { type: 'sources'; sources: ExternalSource[] }
  | { type: 'answer_delta'; text: string }
  | { type: 'answer_reset' }
  | { type: 'answer'; text: string; confidence: AskConfidence; follow_ups?: string[] }
  | { type: 'follow_ups'; follow_ups: string[] }
  | { type: 'done'; iterations: number; durationMs: number; sessionId: string };

export interface AskApi {
  streamAsk: (params: {
    question: string;
    sessionId: string;
    signal?: AbortSignal;
    onEvent: (ev: AskStreamEvent) => void;
  }) => Promise<void>;
  listConversations: (params?: {
    q?: string;
    limit?: number;
    cursor?: string;
  }) => Promise<AskConversationListResponse>;
  getConversation: (id: string) => Promise<AskConversationDetailResponse>;
  renameConversation: (id: string, title: string) => Promise<AskRenameResponse>;
  deleteConversation: (id: string) => Promise<void>;
}

export function createAskApi(client: ApiClient): AskApi {
  const baseUrl = client.baseUrl;
  const apiKey = client.apiKey;

  return {
    streamAsk: async ({ question, sessionId, signal, onEvent }): Promise<void> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (apiKey) headers['X-API-Key'] = apiKey;
      const csrf = askCsrfTokenProvider?.();
      if (csrf) headers['X-CSRF-Token'] = csrf;

      const response = await fetch(`${baseUrl}/api/v1/ask`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ question, sessionId }),
        signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Request failed: ${String(response.status)}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const { frames, remainder } = parseSseFrames(buffer);
          buffer = remainder;

          for (const frame of frames) {
            try {
              const payload = JSON.parse(frame.data) as Record<string, unknown>;
              const ev = { type: frame.event, ...payload } as AskStreamEvent;
              onEvent(ev);
              if (ev.type === 'done') return;
            } catch {
              continue;
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    },

    listConversations: (params): Promise<AskConversationListResponse> => {
      return client.get<AskConversationListResponse>('/api/v1/ask/conversations', {
        query: {
          q: params?.q,
          limit: params?.limit,
          cursor: params?.cursor,
        },
      });
    },

    getConversation: (id): Promise<AskConversationDetailResponse> => {
      return client.get<AskConversationDetailResponse>(
        `/api/v1/ask/conversations/${encodeURIComponent(id)}`,
      );
    },

    renameConversation: (id, title): Promise<AskRenameResponse> => {
      return client.patch<AskRenameResponse>(
        `/api/v1/ask/conversations/${encodeURIComponent(id)}`,
        { body: { title } },
      );
    },

    deleteConversation: (id): Promise<void> => {
      return client.del<void>(`/api/v1/ask/conversations/${encodeURIComponent(id)}`);
    },
  };
}
